import {
  type GameHold,
  type GameHoldOutcomeFilter,
  HOLD_OUTCOME_MEANINGS,
  type HoldOutcome,
  type LedgerPerson,
} from "@pax/api-contract/accounting/v1alpha1";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { readableCode } from "@/features/credits/journals";
import { Picker } from "@/features/evals/Bubbles";
import { Disclosure, Section } from "@/features/evals/components";
import { Problem } from "@/features/staff/components";
import type { HistoryWindow } from "./balances";
import {
  CopyId,
  Credits,
  EntryTable,
  ExpandAllToggle,
  NotCharged,
  Note,
  OutcomeTag,
  Pager,
  type PageSize,
  PageSizePicker,
  PersonName,
  pickedRow,
  When,
} from "./components";
import { agentLabel, exactMoment, outcomeLabels } from "./format";
import { GameSpendGraph } from "./GameSpendGraph";
import { GameIdFilter, PlacedWindowPicker, UserIdFilter } from "./pickers";
import {
  fetchGameHoldsNewerStart,
  type GameHoldsFilter,
  gameHoldsQuery,
  gameInfoQuery,
} from "./queries";
import { foldRuns } from "./runs";
import type { GraphSettings } from "./TimeGraph";

interface GameHoldsProps {
  filter: GameHoldsFilter;
  onFilterChange: (filter: GameHoldsFilter) => void;
  /** The holds start just older than this hold. Missing starts at the newest. */
  start: string | undefined;
  onStartChange: (start: string | undefined) => void;
  pageSize: PageSize;
  onPageSizeChange: (pageSize: PageSize) => void;
  /** Every hold on its own row, with no back-to-back alike holds folded together. */
  expandAll: boolean;
  onExpandAllChange: (expandAll: boolean) => void;
  graph: GraphSettings;
  onGraphChange: (patch: Partial<GraphSettings>) => void;
}

/** One game by its id: who owns it, what its holds spent and who paid, and each hold. */
export function GameView({
  gameId,
  onGameChange,
  ...holds
}: GameHoldsProps & {
  gameId: string | undefined;
  onGameChange: (gameId: string | undefined) => void;
}) {
  return (
    <>
      <div className="-ml-3 flex flex-wrap items-center gap-1">
        <GameIdFilter gameId={gameId} onChange={onGameChange} />
      </div>
      {gameId ? (
        <Game key={gameId} gameId={gameId} {...holds} />
      ) : (
        <Note>Paste a game id to see its holds.</Note>
      )}
    </>
  );
}

function Game({ gameId, ...holds }: GameHoldsProps & { gameId: string }) {
  const info = useQuery(gameInfoQuery(gameId));
  const page = useQuery(
    gameHoldsQuery(gameId, holds.filter, { before: holds.start, limit: holds.pageSize }),
  );
  const game = info.data;
  const deleted = info.isSuccess && game === null;
  /** A picked span, and the start the list jumped to for it; none when it only stands out. */
  const [picked, setPicked] = useState<{ window: HistoryWindow; start?: string } | null>(null);
  const pick = (window: HistoryWindow, start: string) => {
    setPicked({ window, start });
    holds.onStartChange(start);
  };
  const jumped = picked?.start !== undefined;
  return (
    <>
      <Problem error={info.error} />
      {(game || deleted) && (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-0.5 text-sm">
          {[
            [
              "Title",
              <span key="title" className="inline-flex items-baseline gap-1.5" title={gameId}>
                {game?.title ?? "Deleted game"}
                <CopyId id={gameId} label="game id" />
              </span>,
            ],
            [
              "Preset",
              game
                ? game.preset.title
                : (presetTitleFromHolds(page.data?.holds ?? []) ?? "Untitled preset"),
            ],
            ["Owner", game ? <PersonName key="owner" person={game.owner} /> : "None"],
          ].map(([term, detail]) => (
            <div key={String(term)} className="contents">
              <dt className="text-muted-foreground">{term}</dt>
              <dd className="min-w-0 [overflow-wrap:anywhere]">{detail}</dd>
            </div>
          ))}
        </dl>
      )}
      <Section label="Cost">
        <Disclosure
          className="border-t border-foreground/[0.07] py-1"
          summary="Spend over time"
          open={holds.graph.graph === true}
          onOpenChange={open => holds.onGraphChange({ graph: open || undefined })}
        >
          {() => (
            <GameSpendGraph
              gameId={gameId}
              filter={holds.filter}
              pageSize={holds.pageSize}
              settings={holds.graph}
              onSettingsChange={holds.onGraphChange}
              onSelect={pick}
              onHighlight={window => setPicked({ window })}
            />
          )}
        </Disclosure>
      </Section>
      <GameHolds
        gameId={gameId}
        {...holds}
        picked={picked && (!jumped || picked.start === holds.start) ? picked.window : null}
      />
    </>
  );
}

function presetTitleFromHolds(holds: readonly GameHold[]) {
  if (!holds[0]) return "None";
  return holds.find(hold => hold.presetTitle)?.presetTitle ?? undefined;
}

const holdOutcomes = [
  "settled",
  "released",
  "abandoned",
  "open",
] as const satisfies readonly HoldOutcome[];

const outcomeOptions: { value: GameHoldOutcomeFilter | ""; label: string }[] = [
  { value: "", label: "Every outcome" },
  ...holdOutcomes.map(outcome => ({
    value: outcome,
    label: outcomeLabels[outcome],
  })),
];

/** The game's holds, newest placed first, by outcome, payer, and placing window, a page at a time. */
function GameHolds({
  gameId,
  filter,
  onFilterChange,
  start,
  onStartChange,
  pageSize,
  onPageSizeChange,
  expandAll,
  onExpandAllChange,
  picked,
}: GameHoldsProps & {
  gameId: string;
  /** The span a click on the spend graph picked, whose rows stand out. */
  picked: HistoryWindow | null;
}) {
  const listTop = useRef<HTMLDivElement>(null);
  const inPicked = (hold: GameHold) => {
    const at = Date.parse(hold.placedAt);
    return !!picked && at >= picked.from && at < picked.to;
  };
  const [leaving, setLeaving] = useState(false);
  const holds = useQuery(gameHoldsQuery(gameId, filter, { before: start, limit: pageSize }));
  const rows = holds.data?.holds ?? [];
  const next = holds.data?.nextBeforeHoldId;
  const toNewer = async (before: string) => {
    setLeaving(true);
    try {
      onStartChange(await fetchGameHoldsNewerStart(gameId, filter, { before, limit: pageSize }));
    } finally {
      setLeaving(false);
    }
  };
  const pager = {
    label: holdsShown(rows),
    loading: holds.isPlaceholderData || leaving,
    toNewest: start ? () => onStartChange(undefined) : undefined,
    toNewer: start ? () => void toNewer(start) : undefined,
    toOlder: next ? () => onStartChange(next) : undefined,
  };
  return (
    <Section label="Holds">
      <div className="-ml-3 mb-1 flex flex-wrap items-center gap-1">
        <Picker
          label="Outcome"
          value={filter.outcome ?? ""}
          options={outcomeOptions}
          searchable
          onChange={outcome => onFilterChange({ ...filter, outcome: outcome || undefined })}
        />
        <PayerFilter
          payerUserId={filter.payerUserId}
          payers={payersOn(rows)}
          onChange={payerUserId => onFilterChange({ ...filter, payerUserId })}
        />
        <PlacedWindowPicker
          from={filter.from}
          to={filter.to}
          onChange={window => onFilterChange({ ...filter, ...window })}
        />
        <span className="ml-3">
          <PageSizePicker pageSize={pageSize} onChange={onPageSizeChange} />
        </span>
        <span className="ml-2">
          <ExpandAllToggle expandAll={expandAll} onChange={onExpandAllChange} />
        </span>
      </div>
      <div ref={listTop} className="scroll-mt-4" />
      <Pager {...pager} />
      <Problem error={holds.error} />
      {holds.isSuccess && rows.length === 0 && <Note>No holds match</Note>}
      {foldRuns(rows, holdRunKey, expandAll).map(row =>
        row.kind === "run" ? (
          <HoldRunCard key={row.items[0].holdId} holds={row.items} inPicked={inPicked} />
        ) : (
          <HoldCard key={row.item.holdId} hold={row.item} picked={inPicked(row.item)} />
        ),
      )}
      <Pager {...pager} scrollTo={listTop} />
    </Section>
  );
}

/** How many holds the page shows. */
function holdsShown(holds: readonly GameHold[]) {
  if (holds.length === 0) return "Nothing here";
  return `${holds.length} ${holds.length === 1 ? "hold" : "holds"} shown`;
}

/** Unique payers on the holds already loaded, in the order they first appear. */
function payersOn(holds: readonly GameHold[]) {
  const seen = new Map<string, LedgerPerson>();
  for (const hold of holds) {
    for (const payer of hold.payers) {
      if (!seen.has(payer.userId)) seen.set(payer.userId, payer);
    }
  }
  return [...seen.values()];
}

/** How a payer reads in the player filter. */
function payerOptionLabel(person: { displayName: string | null; username: string | null }) {
  const name = person.displayName ?? "Unknown";
  return person.username ? `${name} @${person.username}` : name;
}

/** All players, or one payer: picked from the loaded rows, or by any user id. */
function PayerFilter({
  payerUserId,
  payers,
  onChange,
}: {
  payerUserId?: string;
  payers: readonly LedgerPerson[];
  onChange: (payerUserId: string | undefined) => void;
}) {
  const listed = payers.some(payer => payer.userId === payerUserId);
  return (
    <>
      <Picker
        label="Payer"
        value={payerUserId ?? ""}
        options={[
          { value: "", label: "All players" },
          ...payers.map(payer => ({
            value: payer.userId,
            label: payerOptionLabel(payer),
          })),
          ...(!payerUserId || listed ? [] : [{ value: payerUserId, label: payerUserId }]),
        ]}
        onChange={value => onChange(value || undefined)}
      />
      <UserIdFilter
        userId={payerUserId}
        onChange={onChange}
        placeholder="Filter to a payer's user id"
      />
    </>
  );
}

/** How a hold ended, its kind and agent, and who paid for it, the run it can join. */
function holdRunKey(hold: GameHold) {
  return `${hold.outcome}|${hold.kind}|${hold.agent?.versionId ?? ""}|${hold.payers
    .map(payer => payer.userId)
    .sort()
    .join(",")}`;
}

/** What a hold cost, what it holds while open, or that it was released with nothing charged. */
function HoldAmount({
  outcome,
  raw,
}: {
  outcome: HoldOutcome;
  raw: { measured: string | null; estimate: string };
}) {
  if (raw.measured) return <Credits raw={raw.measured} />;
  if (outcome === "open") {
    return (
      <span className="text-muted-foreground">
        <Credits raw={raw.estimate} /> held
      </span>
    );
  }
  return <NotCharged reservedRaw={raw.estimate} />;
}

/** What a run's holds cost in total, or what they hold or held when none of them was charged. */
function runAmount(holds: readonly GameHold[]) {
  const charged = holds.some(hold => hold.measuredRaw !== null);
  const raw = holds
    .reduce(
      (total, hold) => total + BigInt((charged ? hold.measuredRaw : hold.estimateRaw) ?? "0"),
      0n,
    )
    .toString();
  return charged ? { measured: raw, estimate: raw } : { measured: null, estimate: raw };
}

/**
 * Back-to-back holds that ended the same way for the same payers, kind, and agent, newest first: how many,
 * the kind, what they cost or held in total, and when. Open it for each hold's time and amount.
 */
function HoldRunCard({
  holds,
  inPicked,
}: {
  holds: readonly [GameHold, ...GameHold[]];
  inPicked: (hold: GameHold) => boolean;
}) {
  const newest = holds[0];
  const oldest = holds.at(-1) ?? newest;
  return (
    <Disclosure
      stacked
      className={`border-t border-foreground/[0.07] py-1 ${holds.some(inPicked) ? pickedRow : ""}`}
      summary={
        <span className="flex items-baseline justify-between gap-4">
          <span className="min-w-0">
            <span className="block truncate">
              {holds.length} holds
              <span className="text-muted-foreground">
                {" "}
                · {agentLabel(newest.agent) ?? readableCode(newest.kind)}
              </span>
              <OutcomeTag outcome={newest.outcome} />
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {newest.payers.map(payer => payer.displayName).join(", ")}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end">
            <HoldAmount outcome={newest.outcome} raw={runAmount(holds)} />
            <span className="text-xs text-muted-foreground">
              <When iso={oldest.placedAt} /> – <When iso={newest.placedAt} />
            </span>
          </span>
        </span>
      }
    >
      {() => (
        <div className="pb-2">
          {holds.map(hold => (
            <Disclosure
              key={hold.holdId}
              className={`border-t border-foreground/[0.07] ${inPicked(hold) ? pickedRow : ""}`}
              summary={
                <span className="flex items-baseline justify-between gap-4 text-xs">
                  <span className="min-w-0 truncate">
                    <When iso={hold.placedAt} />
                  </span>
                  <HoldAmount
                    outcome={hold.outcome}
                    raw={{ measured: hold.measuredRaw, estimate: hold.estimateRaw }}
                  />
                </span>
              }
            >
              {() => <HoldDetails hold={hold} />}
            </Disclosure>
          ))}
        </div>
      )}
    </Disclosure>
  );
}

/** How one hold ended, what it cost or reserved, and who paid; open it for the settlement. */
function HoldCard({ hold, picked }: { hold: GameHold; picked: boolean }) {
  return (
    <Disclosure
      className={`border-t border-foreground/[0.07] py-1 ${picked ? pickedRow : ""}`}
      summary={
        <span className="flex items-baseline justify-between gap-4">
          <span className="min-w-0">
            <span className="block truncate">
              {agentLabel(hold.agent) ?? readableCode(hold.kind)}
              <OutcomeTag outcome={hold.outcome} />
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {hold.payers.map(payer => payer.displayName).join(", ")}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end">
            <HoldAmount
              outcome={hold.outcome}
              raw={{ measured: hold.measuredRaw, estimate: hold.estimateRaw }}
            />
            <span className="text-xs text-muted-foreground">
              <When iso={hold.placedAt} />
            </span>
          </span>
        </span>
      }
    >
      {() => <HoldDetails hold={hold} />}
    </Disclosure>
  );
}

function HoldDetails({ hold }: { hold: GameHold }) {
  return (
    <div className="space-y-3 pb-2">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-0.5 text-xs">
        {[
          ["Outcome", `${outcomeLabels[hold.outcome]}: ${HOLD_OUTCOME_MEANINGS[hold.outcome]}`],
          ["Hold request", <code key="request">{hold.holdRequestId}</code>],
          ["Hold", hold.holdId],
          ["Kind", <code key="kind">{hold.kind}</code>],
          ["Deadline", exactMoment(hold.expiresAt)],
          ...(hold.releaseReason ? [["Release reason", outcomeLabels[hold.releaseReason]]] : []),
          ...(hold.agent ? [["Agent", agentLabel(hold.agent)]] : []),
          ["Reserved", <Credits key="reserved" raw={hold.estimateRaw} />],
          [
            "Payers",
            <span key="payers" className="block space-y-0.5">
              {hold.payers.map(payer => (
                <span key={payer.userId} className="block">
                  <PersonName person={payer} />
                </span>
              ))}
            </span>,
          ],
        ].map(([term, detail]) => (
          <div key={String(term)} className="contents">
            <dt className="text-muted-foreground">{term}</dt>
            <dd className="min-w-0 [overflow-wrap:anywhere]">{detail}</dd>
          </div>
        ))}
      </dl>
      {hold.settlement?.entries.map(entry => (
        <EntryTable key={entry.entryId} entry={entry} />
      ))}
    </div>
  );
}
